import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { Search, Plus, Eye, Sparkles, Filter, Flame, Skull, Droplet, Sun, Leaf, Sparkle, Scroll, Swords, PawPrint, Wrench, Globe2, Zap, ShieldPlus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useScryfallSearch } from '@/hooks/useScryfall';
import { useToast } from '@/hooks/useToast';
import type { ScryfallCard } from '@/types/scryfall';
import { CardDetailsDialog } from '@/components/CardDetailsDialog';
import { SortControls } from '@/components/SortControls';
import RandomCardSuggestions from '@/components/RandomCardSuggestions';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { scryfallFormats } from '@/lib/formats';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

const manaSymbols = {
  W: <Sun />,
  U: <Droplet />,
  B: <Skull />,
  R: <Flame />,
  G: <Leaf />,
  C: <Sparkle />,
};

export function CardSearch({ addCard, cards: _cards, sideboardCards: _sideboardCards, searchQuery, setSearchQuery, initialResults, initialSort }: { addCard: (card: ScryfallCard) => void, cards: ScryfallCard[], sideboardCards?: ScryfallCard[], searchQuery: string, setSearchQuery: (query: string) => void, initialResults?: ScryfallCard[], initialSort?: { sortBy: string, sortOrder: string } }) {
  const [, setSearchParams] = useSearchParams();
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);
  const [sortBy, setSortBy] = useState(initialSort?.sortBy || 'name');
  const [sortOrder, setSortOrder] = useState(initialSort?.sortOrder || 'auto');

  // Filter states are derived from the search query
  const [lang, setLang] = useState('en');
  const [format, setFormat] = useState('any');
  const [colorIdentity, setColorIdentity] = useState<string[]>([]);
  const [cardType, setCardType] = useState<string[]>([]);
  const [cmc, setCmc] = useState('');
  const [cmcOperator, setCmcOperator] = useState('=');

  const cardTypeIcons = {
    creature: <PawPrint />,
    artifact: <Wrench />,
    enchantment: <Sparkles />,
    land: <Globe2 />,
    instant: <Zap />,
    sorcery: <Scroll />,
    planeswalker: <ShieldPlus />,
    battle: <Swords />
  };

  useEffect(() => {
    if (initialSort) {
      setSortBy(initialSort.sortBy);
      setSortOrder(initialSort.sortOrder);
    }
  }, [initialSort]);

  // Two-way binding: Parse search query to update filter UI state
  useEffect(() => {
    const formatMatch = searchQuery.match(/f:\s*(\w+)/);
    setFormat(formatMatch ? formatMatch[1] : 'any');

    const colorMatch = searchQuery.match(/(?:ci|id):\s*([wubrgc]+)/i);
    setColorIdentity(colorMatch ? colorMatch[1].toUpperCase().split('') : []);

    const typeMatches = [...searchQuery.matchAll(/t:\s*(\w+)/g)];
    setCardType(typeMatches.map(match => match[1].toLowerCase()));

    const cmcMatch = searchQuery.match(/cmc\s*([<>=]{1,2})\s*(\d+)/);
    setCmcOperator(cmcMatch ? cmcMatch[1] : '=');
    setCmc(cmcMatch ? cmcMatch[2] : '');
  }, [searchQuery]);

  const { toast } = useToast();

  const {
    data: searchResults,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useScryfallSearch(
    debouncedQuery ? `${debouncedQuery} lang:${lang}` : '',
    sortBy,
    sortOrder,
    initialResults ? { object: 'list', data: initialResults, total_cards: initialResults.length, has_more: false } : undefined,
    debouncedQuery.trim().length > 0
  );
  const [detailsCard, setDetailsCard] = useState<ScryfallCard | null>(null);

  const observer = useRef<IntersectionObserver>();
  const lastCardRef = useCallback((node: HTMLDivElement) => {
    if (isLoading || isFetchingNextPage) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasNextPage) {
        fetchNextPage();
      }
    });
    if (node) observer.current.observe(node);
  }, [isLoading, isFetchingNextPage, hasNextPage, fetchNextPage]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
      if (searchQuery) {
        setSearchParams({ q: searchQuery });
      } else {
        setSearchParams({});
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, setSearchParams]);

  // Handlers to update search query from filter UI
  const handleFormatChange = (newFormat: string) => {
    const currentQuery = searchQuery.replace(/f:\s*\w+\s*/g, '').trim();
    const newQuery = newFormat === 'any' ? currentQuery : `${currentQuery} f:${newFormat}`.trim();
    setSearchQuery(newQuery);
  };

  const handleColorChange = (newColors: string[]) => {
    const currentQuery = searchQuery.replace(/(?:ci|id):\s*[wubrgc]+\s*/gi, '').trim();
    const newQuery = newColors.length === 0 ? currentQuery : `${currentQuery} ci:${newColors.join('')}`.trim();
    setSearchQuery(newQuery);
  };

  const handleCardTypeChange = (newTypes: string[]) => {
    const currentQuery = searchQuery.replace(/t:\s*\w+\s*/g, '').trim();
    const newQuery = newTypes.length === 0 ? currentQuery : `${currentQuery} ${newTypes.map(t => `t:${t}`).join(' ')}`.trim();
    setSearchQuery(newQuery);
  };

  const handleCmcChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCmc = e.target.value;
    const currentQuery = searchQuery.replace(/cmc\s*[<>=]{1,2}\s*\d+\s*/g, '').trim();
    const newQuery = newCmc ? `${currentQuery} cmc${cmcOperator}${newCmc}`.trim() : currentQuery;
    setSearchQuery(newQuery);
  };

  const handleCmcOperatorChange = (newOperator: string) => {
    const currentQuery = searchQuery.replace(/cmc\s*[<>=]{1,2}\s*\d+\s*/g, '').trim();
    const newQuery = cmc ? `${currentQuery} cmc${newOperator}${cmc}`.trim() : currentQuery;
    setSearchQuery(newQuery);
  };

  const handleAddCard = (card: ScryfallCard) => {
    addCard(card);
    toast({
      title: "Card added",
      description: `${card.name} added to surveilled cards.`,
    });
  };

  const getCardImageUrl = (card: ScryfallCard): string => {
    if (card.image_uris?.normal) {
      return card.image_uris.normal;
    }
    if (card.card_faces?.[0]?.image_uris?.normal) {
      return card.card_faces[0].image_uris.normal;
    }
    return '';
  };

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'mythic': return 'bg-gradient-to-r from-orange-500/20 to-red-500/20 text-orange-400 border-orange-500/30';
      case 'rare': return 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-400 border-yellow-500/30';
      case 'uncommon': return 'bg-gradient-to-r from-gray-400/20 to-gray-500/20 text-gray-300 border-gray-400/30';
      case 'common': return 'bg-gradient-to-r from-gray-600/20 to-gray-700/20 text-gray-400 border-gray-600/30';
      default: return 'bg-gradient-to-r from-gray-500/20 to-gray-600/20 text-gray-400 border-gray-500/30';
    }
  };

  const allCards = searchResults?.pages.flatMap(page => page.data) ?? [];
  const totalCards = searchResults?.pages[0]?.total_cards ?? 0;

  return (
    <div className="space-y-2">
      {/* Search Input */}
      <div className="relative group">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-primary/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="relative">
          <Input
            placeholder="Search for Magic cards..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 sm:pl-12 pr-12 py-3 sm:py-4 text-base sm:text-lg bg-background/80 backdrop-blur-sm border-border/50 rounded-2xl focus:border-primary/50 transition-all duration-200 focus:!ring-0 focus:!ring-offset-0"
          />
          <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5 pointer-events-none" />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full">
                  <Filter className="h-5 w-5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80">
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <h4 className="font-medium leading-none">Filters</h4>
                    <p className="text-sm text-muted-foreground">
                      Customize your card search.
                    </p>
                  </div>
                  <div className="grid gap-2">
                    <div className="grid grid-cols-3 items-center gap-4">
                      <Label htmlFor="language">Language</Label>
                      <Select value={lang} onValueChange={setLang}>
                        <SelectTrigger className="w-full col-span-2">
                          <SelectValue placeholder="Language" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="en">English</SelectItem>
                          <SelectItem value="es">Spanish</SelectItem>
                          <SelectItem value="fr">French</SelectItem>
                          <SelectItem value="de">German</SelectItem>
                          <SelectItem value="it">Italian</SelectItem>
                          <SelectItem value="pt">Portuguese</SelectItem>
                          <SelectItem value="ja">Japanese</SelectItem>
                          <SelectItem value="ko">Korean</SelectItem>
                          <SelectItem value="ru">Russian</SelectItem>
                          <SelectItem value="zhs">Simplified Chinese</SelectItem>
                          <SelectItem value="zht">Traditional Chinese</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-3 items-center gap-4">
                      <Label htmlFor="format">Format</Label>
                      <Select value={format} onValueChange={handleFormatChange}>
                        <SelectTrigger className="w-full col-span-2">
                          <SelectValue placeholder="Any Format" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any Format</SelectItem>
                          {scryfallFormats.map(f => (
                            <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-3 items-center gap-4">
                      <Label>Mana Value</Label>
                      <div className="col-span-2 flex gap-2">
                        <Select value={cmcOperator} onValueChange={handleCmcOperatorChange}>
                          <SelectTrigger className="w-1/3">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="=">=</SelectItem>
                            <SelectItem value=">">&gt;</SelectItem>
                            <SelectItem value="<">&lt;</SelectItem>
                            <SelectItem value=">=">&gt;=</SelectItem>
                            <SelectItem value="<=">&lt;=</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          value={cmc}
                          onChange={handleCmcChange}
                          placeholder="Any"
                          className="w-2/3"
                        />
                      </div>
                    </div>
                    <div className="space-y-2 items-center gap-4">
                      <Label>Color Identity</Label>
                      <div className="col-span-2">
                        <ToggleGroup type="multiple" value={colorIdentity} onValueChange={handleColorChange} variant="outline" className="flex-wrap">
                          {Object.entries(manaSymbols).map(([color, icon]) => (
                            <ToggleGroupItem key={color} value={color} className="w-1/4 h-10">
                              {icon}
                            </ToggleGroupItem>
                          ))}
                        </ToggleGroup>
                      </div>
                    </div>
                    <div className="space-y-2 items-center gap-4">
                      <Label>Card Type</Label>
                      <div className="col-span-2">
                        <ToggleGroup type="multiple" value={cardType} onValueChange={handleCardTypeChange} variant="outline" className="flex-wrap">
                          {Object.entries(cardTypeIcons).map(([type, icon]) => (
                            <ToggleGroupItem key={type} value={type} className="w-1/5 h-10">
                              {icon}
                            </ToggleGroupItem>
                          ))}
                        </ToggleGroup>
                      </div>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      {/* Results Count & Sort */}
      {totalCards > 0 && (
        <div className="flex items-center justify-between text-xs gap-2 px-2">
          <div className="flex items-center gap-2 text-muted-foreground">
            <div className="w-1.5 h-1.5 bg-primary rounded-full" />
            <span>
              {totalCards.toLocaleString()} cards
            </span>
          </div>
          <SortControls sortBy={sortBy} setSortBy={setSortBy} sortOrder={sortOrder} setSortOrder={setSortOrder} />
        </div>
      )}

      {/* Error State */}
      {error && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-destructive rounded-full" />
              <p className="text-destructive text-sm">Error searching cards. Please try again.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results, Empty State, and Search Tips */}
      <div className="relative">
        <div className={cn("h-[calc(100vh-10rem)] lg:h-[calc(100vh-8rem)] overflow-y-auto sm:p-2", allCards.length > 0 && "h-[calc(100vh-12.5rem)] lg:h-[calc(100vh-10.5rem)]")}>
          {/* Loading State */}
          {isLoading && (
            <div className="space-y-6">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                <span>Searching the multiverse...</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Card key={i} className="overflow-hidden border-border/50">
                    <div className="aspect-[5/7] relative">
                      <Skeleton className="w-full h-full" />
                    </div>
                    <CardContent className="p-4 space-y-3">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                      <div className="flex justify-between">
                        <Skeleton className="h-5 w-16" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
          {/* Results */}
          {allCards.length > 0 && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {allCards.map((card, index) => {
                  const isLastCard = index === allCards.length - 1;
                  return (
                    <div key={card.id} ref={isLastCard ? lastCardRef : null}>
                      <Card className="group overflow-hidden border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 rounded-lg">
                        <div className="aspect-[5/7] relative overflow-hidden rounded-t-lg cursor-pointer" onClick={() => setDetailsCard(card)}>
                          {getCardImageUrl(card) ? (
                            <img
                              src={getCardImageUrl(card)}
                              alt={card.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center">
                              <Eye className="h-12 w-12 text-muted-foreground/50" />
                            </div>
                          )}

                          {/* Overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

                          {/* Add Button */}
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex gap-2">
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); handleAddCard(card); }}
                              className={`transition-all duration-300 opacity-100 lg:opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 bg-background/80 hover:bg-background`}
                            >
                              <Plus className="h-5 w-5" />
                            </Button>

                          </div>
                        </div>

                        <CardContent className="p-3 space-y-1">
                          <div>
                            <h4 className="font-semibold text-foreground text-xs sm:text-sm leading-tight truncate cursor-pointer" onClick={() => setDetailsCard(card)}>
                              {card.name}
                            </h4>
                            <p className="text-[.7rem] sm:text-xs text-muted-foreground truncate">
                              {card.type_line}
                            </p>
                          </div>
                          <div className="flex items-center justify-between pt-1">
                            <Badge
                              variant="outline"
                              className={`text-[.5rem] sm:text-xs font-medium capitalize ${getRarityColor(card.rarity)}`}
                            >
                              {card.rarity}
                            </Badge>
                            {card.mana_cost && (
                              <span className="text-[.7rem] sm:text-xs text-muted-foreground font-mono">
                                {card.mana_cost.includes(' // ') ? card.mana_cost.split(' // ')[0] : card.mana_cost}
                              </span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
              {isFetchingNextPage && (
                <div className="flex justify-center items-center py-4">
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <span className="ml-2 text-muted-foreground">Loading more...</span>
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {allCards.length === 0 && debouncedQuery && !isLoading && (
            <Card className="border-dashed border-border/50">
              <CardContent className="py-16 px-8 text-center">
                <div className="max-w-sm mx-auto space-y-6">
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-muted to-muted/50 rounded-2xl flex items-center justify-center mx-auto">
                      <Eye className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-destructive/20 rounded-full flex items-center justify-center">
                      <span className="text-xs text-destructive">?</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-foreground">No cards found</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      Try adjusting your search terms or check for typos. You can search by name, type, mana cost, and more.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Search Tips */}
          {!searchQuery && (
            <div className="space-y-4">
              <Card className="border-dashed border-border/50 bg-accent/20">
                <CardContent className="py-8 px-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Sparkles className="h-5 w-5 text-primary" />
                      <h3 className="font-semibold text-foreground">Search Tips</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="space-y-2">
                        <p className="text-muted-foreground">
                          <span className="font-medium text-foreground">Card names:</span> "Lightning Bolt"
                        </p>
                        <p className="text-muted-foreground">
                          <span className="font-medium text-foreground">Types:</span> type:creature, type:instant
                        </p>
                      </div>
                      <div className="space-y-2">
                        <p className="text-muted-foreground">
                          <span className="font-medium text-foreground">Mana cost:</span> cmc:3, cmc&gt;=4
                        </p>
                        <p className="text-muted-foreground">
                          <span className="font-medium text-foreground">Colors:</span> color:red, color:blue
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Random Card Suggestions */}
          {!searchQuery && (
            <RandomCardSuggestions addCard={addCard} />
          )}
        </div>
        <div className={cn("absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background to-transparent pointer-events-none", !searchQuery && "hidden")} />
      </div>
      {detailsCard && (
        <CardDetailsDialog
          card={detailsCard}
          open={!!detailsCard}
          onOpenChange={(open) => !open && setDetailsCard(null)}
          onAddToList={() => handleAddCard(detailsCard)}
        />
      )}
    </div>
  );
}
